import React, { useCallback, useEffect, useRef, useState } from 'react';
import { EmptyTable, Paginate } from 'Shared/components';
import { useBlockListAttributes } from 'Shared/services/Reznor/GetBlockListAttributes';
import type { AttributeTypeMetadata } from 'Shared/services/Reznor/GetAttributeTypes';
import { DEFAULT_PAGE_SIZE } from 'BlockList/constants';
import { BlockListTable } from 'BlockList/components/BlockListTable';
import {
  BlockListFilters,
  BlockListFiltersState,
} from 'BlockList/components/BlockListFilters';
import { useConfirmDeactivate } from 'BlockList/hooks/useConfirmDeactivate';
import { normalizeSearchQuery } from 'BlockList/utils/normalizeSearchQuery';
import styles from './BlockListPanel.module.css';

const DEFAULT_FILTERS: BlockListFiltersState = {
  q: '',
  attributeType: '',
  reason: '',
  status: 'active',
};

const hasActiveFilters = (filters: BlockListFiltersState): boolean =>
  !!(
    filters.q ||
    filters.attributeType ||
    filters.reason ||
    filters.status !== 'active'
  );

type BlockListPanelProps = {
  // Incremented by the shell after a new attribute is created via the
  // Add attribute modal. Each new value triggers a refetch on page 1.
  refreshKey?: number;
  // Attribute-type catalog fetched once by the shell and threaded down to
  // the filters so this panel doesn't re-fetch /blocklist/attribute_types.
  attributeTypes?: AttributeTypeMetadata[];
};

export const BlockListPanel: React.FC<BlockListPanelProps> = ({
  refreshKey = 0,
  attributeTypes = [],
}) => {
  const [filters, setFilters] =
    useState<BlockListFiltersState>(DEFAULT_FILTERS);
  const [offset, setOffset] = useState(0);

  const page = Math.floor(offset / DEFAULT_PAGE_SIZE) + 1;
  const normalizedQ = normalizeSearchQuery(filters.q, filters.attributeType);
  const serviceFilters = {
    q: normalizedQ || undefined,
    attribute_type: filters.attributeType || undefined,
    reason: filters.reason || undefined,
    status: (filters.status || undefined) as
      | 'active'
      | 'inactive'
      | 'all'
      | undefined,
    page,
    per_page: DEFAULT_PAGE_SIZE,
  };

  const { data, meta, loading, error, refetch } =
    useBlockListAttributes(serviceFilters);

  const { openDeactivate, modal } = useConfirmDeactivate({
    onSuccess: refetch,
  });

  // React to shell-driven refreshes (after a new attribute is created).
  const prevRefreshKey = useRef(refreshKey);
  useEffect(() => {
    if (prevRefreshKey.current !== refreshKey) {
      prevRefreshKey.current = refreshKey;
      setOffset(0);
      refetch();
    }
  }, [refreshKey, refetch]);

  const handleFiltersChange = useCallback(
    (newFilters: BlockListFiltersState) => {
      setFilters(newFilters);
      setOffset(0);
    },
    []
  );

  const handleOffsetChange = useCallback(
    (_limit: number, newOffset: number) => {
      setOffset(newOffset);
    },
    []
  );

  const totalCount = meta.totalCount;
  const showingStart = totalCount === 0 ? 0 : offset + 1;
  const showingEnd = Math.min(offset + DEFAULT_PAGE_SIZE, totalCount);
  const filtersActive = hasActiveFilters(filters);

  // Always render the table chrome (headers, etc.). Error and empty
  // states render inside the body via EmptyTable so the page never
  // "jumps" between table and bare-text layouts.
  const hasRows = !error && data.length > 0;
  const showEmpty = !loading && !hasRows;

  const emptyMessage = error
    ? error
    : filtersActive
      ? 'No attributes match these filters'
      : 'No block list entries yet. Use Add attribute to add one manually.';

  return (
    <>
      <BlockListFilters
        filters={filters}
        onChange={handleFiltersChange}
        attributeTypes={attributeTypes}
      />

      <BlockListTable
        rows={hasRows ? data : []}
        loading={loading}
        onDeactivate={openDeactivate}
      />

      {showEmpty && <EmptyTable>{emptyMessage}</EmptyTable>}

      {hasRows && (
        <div className={styles.paginationFooter}>
          <p
            className={styles.resultCount}
            aria-live="polite"
            aria-atomic="true"
          >
            {totalCount > 0
              ? `Showing ${showingStart.toLocaleString()}–${showingEnd.toLocaleString()} of ${totalCount.toLocaleString()}`
              : ''}
          </p>
          <Paginate
            limit={DEFAULT_PAGE_SIZE}
            offset={offset}
            total={totalCount}
            onOffsetChange={handleOffsetChange}
            className={styles.paginateFlat}
          />
        </div>
      )}
      {modal}
    </>
  );
};
